import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Card,
  CardContent,
  Divider,
  LinearProgress
} from '@mui/material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useUser } from '../../contexts/UserContext';
import { db } from '../../firebaseClient';
import { provisionTenant } from '../../utils/firebaseCalls';
import { useWaitForTenantAssignment } from '../../hooks/useClaimsPolling';

// Add type for search result
interface SearchResult {
  id: string;
  name: string;
  createdAt: any;
}

// Add window type extension for timeout
declare global {
  interface Window {
    orgSearchTimeout?: NodeJS.Timeout;
  }
}

const AutoProvision: React.FC = () => {
  const [organizationName, setOrganizationName] = useState<string>('');
  const [referralCode, setReferralCode] = useState<string>('');
  const [agreedToTerms, setAgreedToTerms] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSimilarOrgs, setShowSimilarOrgs] = useState<boolean>(false);
  const [selectedExistingOrg, setSelectedExistingOrg] = useState<SearchResult | null>(null);
  const [userWantsToExit, setUserWantsToExit] = useState<boolean>(false);
  const [pollingProgress, setPollingProgress] = useState<number>(0);

  const { user, userProfile, signOutUser, refreshUserClaims } = useUser();
  const navigate = useNavigate();
  const { waitForTenant, isPolling, attempts } = useWaitForTenantAssignment();

  const steps = [
    'Organization Details',
    'Account Setup',
    'Welcome!'
  ];

  useEffect(() => {
    // If user is not authenticated, redirect to login
    if (!user) {
      navigate('/login');
      return;
    }

    // Only auto-redirect if user hasn't explicitly chosen to stay on this page
    if (user.tenantId && !userWantsToExit) {
      // Don't auto-redirect immediately, let user see the options first
      // This prevents the jarring auto-redirect experience
    }
  }, [user, navigate, userWantsToExit]);

  const searchSimilarOrganizations = async (orgName: string) => {
    if (!orgName.trim() || orgName.length < 2) {
      setSearchResults([]);
      setShowSimilarOrgs(false);
      return;
    }

    try {
      const orgNameLower = orgName.toLowerCase();

      // Search for organizations with similar names
      const tenantsQuery = query(
        collection(db, 'tenants'),
        where('name', '>=', orgName),
        where('name', '<=', orgName + '\uf8ff')
      );

      const querySnapshot = await getDocs(tenantsQuery);
      const results: SearchResult[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        // Additional fuzzy matching for better results
        if (data.name.toLowerCase().includes(orgNameLower) ||
          orgNameLower.includes(data.name.toLowerCase())) {
          results.push({
            id: doc.id,
            name: data.name,
            createdAt: data.createdAt
          });
        }
      });

      setSearchResults(results);
      setShowSimilarOrgs(results.length > 0);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error searching organizations:', error);
      }
      setSearchResults([]);
      setShowSimilarOrgs(false);
    }
  };

  const handleOrganizationNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setOrganizationName(value);
    setSelectedExistingOrg(null);

    // Debounce the search to avoid too many queries
    if (window.orgSearchTimeout) {
      clearTimeout(window.orgSearchTimeout);
    }
    window.orgSearchTimeout = setTimeout(() => {
      searchSimilarOrganizations(value);
    }, 500);
  };

  const handleSelectExistingOrg = (org: SearchResult) => {
    setSelectedExistingOrg(org);
    setOrganizationName(org.name);
    setShowSimilarOrgs(false);
  };

  const handleCancel = async () => {
    // Set flag to prevent auto-redirect interference
    setUserWantsToExit(true);

    setLoading(true);

    try {
      // Sign out the user first
      await signOutUser();

      // Use React Router for smooth navigation
      navigate('/login', { replace: true });

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error signing out:', error);
      }
      setError('Error signing out. Please try again.');
      setLoading(false);
    }
  };

  const handleGoToDashboard = async () => {
    // Set flag to prevent auto-redirect interference
    setUserWantsToExit(true);

    setLoading(true);

    try {
      // Refresh claims to ensure we have latest state
      await refreshUserClaims();

      // Use React Router for smooth navigation
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Error navigating to dashboard:', error);
      // Try to navigate anyway
      navigate('/dashboard', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const handleProvisionTenant = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!organizationName.trim()) {
      setError('Organization name is required');
      return;
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Use and Privacy Policy');
      return;
    }

    // Safely handle referralCode - convert empty strings to null (declare outside try for error logging access)
    const cleanReferralCode = referralCode && referralCode.trim() && referralCode.trim().length > 0 ? referralCode.trim() : null;

    try {
      setError('');
      setLoading(true);
      setCurrentStep(1);

      const userData = {
        name: user?.displayName || 'Unknown User',
        email: user?.email || ''
      };

      console.log('🚀 Starting tenant provisioning...', {
        organizationName: organizationName.trim(),
        hasReferralCode: !!cleanReferralCode,
        userData
      });

      const result: any = await provisionTenant(
        organizationName.trim(),
        cleanReferralCode,
        userData
      );

      console.log('📦 Provisioning result received:', result);

      if (result.success) {
        setCurrentStep(2);

        // Show success message and start polling for tenant assignment
        setError('');

        try {
          // Wait for tenant ID to appear in custom claims (using polling hook)
          await waitForTenant();

          // Update polling progress
          setPollingProgress(100);

          // Refresh user claims in context to trigger re-render
          await refreshUserClaims();

          // Give the UserContext time to update state before navigating
          // This ensures React has time to propagate the state changes
          // and the dashboard loads with the correct user data and menu
          await new Promise(resolve => setTimeout(resolve, 800));

          // Use React Router for smooth navigation (no hard reload)
          navigate('/dashboard', { replace: true });

        } catch (pollingError) {
          console.error('Polling for tenant assignment failed:', pollingError);

          // Fallback: Try one manual refresh and redirect
          try {
            await refreshUserClaims();
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 2000);
          } catch (fallbackError) {
            setError('Account created but redirect failed. Please refresh the page or sign in again.');
            setCurrentStep(0);
          }
        }

      } else {
        console.error('❌ Provisioning failed - Backend returned error:', result);
        const backendError = result.error || result.message || 'Failed to provision tenant';
        console.error('❌ Error message:', backendError);
        console.error('❌ Full result object:', JSON.stringify(result, null, 2));
        throw new Error(backendError);
      }
    } catch (error: any) {
      console.error('❌❌❌ PROVISIONING ERROR ❌❌❌');
      console.error('Error object:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);

      let errorMessage = 'Error setting up account: ' + error.message;

      // Provide more specific error messages
      if (error.code === 'functions/unauthenticated') {
        errorMessage = 'Authentication error. Please try signing out and back in.';
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'Permission denied. Please check your account permissions.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'Cloud function "provisionTenant" not found. The function may not be deployed. Please contact support.';
      } else if (error.code === 'functions/internal') {
        errorMessage = 'Internal server error during account setup. Details: ' + error.message;
      } else if (error.message && error.message.includes('provision')) {
        // Keep the backend error message as-is if it's specific to provisioning
        errorMessage = error.message;
      }

      setError(errorMessage);
      setCurrentStep(0);

      // Log for support/debugging
      console.error('📋 DEBUGGING INFO FOR SUPPORT:');
      console.error('- Organization Name:', organizationName.trim());
      console.error('- Has Referral Code:', !!cleanReferralCode);
      console.error('- User Email:', user?.email);
      console.error('- User UID:', user?.uid);
      console.error('- Error Type:', error.code || 'Unknown');
      console.error('- Error Message:', error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        padding: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 500,
          width: '100%',
          borderRadius: 2,
          position: 'relative'
        }}
      >
        {currentStep === 0 && (
          <>
            <Button
              variant="text"
              size="small"
              onClick={handleCancel}
              sx={{ position: 'absolute', top: 8, right: 8 }}
            >
              ✕ Exit
            </Button>
          </>
        )}

        <Typography variant="h4" component="h1" gutterBottom align="center">
          Welcome, {user.displayName}!
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
          Let's set up your organization to complete your account
        </Typography>

        <Stepper activeStep={currentStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {user.tenantId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You already belong to an organization. You can go directly to your dashboard or contact support if you need to change organizations.
            <Box sx={{ mt: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleGoToDashboard}
                sx={{ mr: 1 }}
              >
                Go to Dashboard
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancel}
              >
                Exit to Login
              </Button>
            </Box>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {currentStep === 0 && (
          <Box component="form" onSubmit={handleProvisionTenant}>
            <Typography variant="h6" gutterBottom>
              Organization Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Complete your account setup by selecting or creating an organization workspace.
            </Typography>

            <TextField
              required
              fullWidth
              id="organizationName"
              label="Organization Name"
              value={organizationName}
              onChange={handleOrganizationNameChange}
              sx={{ mb: 2 }}
              helperText="This will be your tenant/workspace name"
            />

            <TextField
              fullWidth
              id="referralCode"
              label="Referral Code (Optional)"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Enter a referral code to get bonus credits for you and the referrer!"
            />

            {showSimilarOrgs && searchResults.length > 0 && (
              <Card sx={{ mb: 2, bgcolor: 'warning.light' }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Similar organizations found:
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Would you like to join an existing organization or create a new one?
                  </Typography>
                  <List dense>
                    {searchResults.map((org) => (
                      <ListItem key={org.id} disablePadding>
                        <ListItemButton onClick={() => handleSelectExistingOrg(org)}>
                          <ListItemText
                            primary={org.name}
                            secondary={`Created: ${org.createdAt ? new Date(org.createdAt.seconds * 1000).toLocaleDateString() : 'Unknown'}`}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                  <Divider sx={{ my: 1 }} />
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowSimilarOrgs(false)}
                  >
                    Continue with "{organizationName}" as new organization
                  </Button>
                </CardContent>
              </Card>
            )}

            {selectedExistingOrg && (
              <Alert severity="info" sx={{ mb: 2 }}>
                You will join the existing organization "{selectedExistingOrg.name}".
              </Alert>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  I agree to the{' '}
                  <a href="/terms-of-use.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                    Terms of Use
                  </a>
                  {' and '}
                  <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1976d2' }}>
                    Privacy Policy
                  </a>
                </Typography>
              }
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ py: 1.5, mb: 2 }}
              disabled={loading || !agreedToTerms || !organizationName.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Account'}
            </Button>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Cancel
              </Button>
              {user.tenantId && (
                <Button
                  variant="text"
                  onClick={handleGoToDashboard}
                  disabled={loading}
                  sx={{ flex: 1 }}
                >
                  Go to Dashboard
                </Button>
              )}
            </Box>
          </Box>
        )}

        {currentStep === 1 && (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Creating your organization...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Setting up your workspace, configuring permissions, and preparing your starter credits.
            </Typography>
            <LinearProgress sx={{ width: '100%', mt: 2 }} />
          </Box>
        )}

        {currentStep === 2 && (
          <Box sx={{ textAlign: 'center' }}>
            {isPolling ? (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Finalizing setup...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Verifying your account permissions {attempts > 0 && `(${attempts}/20)`}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(attempts / 20) * 100}
                  sx={{ width: '100%', mt: 2 }}
                />
              </>
            ) : (
              <>
                <Typography variant="h6" color="success.main" gutterBottom>
                  ✓ Setup Complete!
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Preparing your workspace...
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Loading menu, settings, and dashboard
                </Typography>
                <CircularProgress size={32} sx={{ mb: 3 }} />

                {/* Manual redirect button as fallback */}
                <Button
                  variant="contained"
                  onClick={async () => {
                    try {
                      await refreshUserClaims();
                      navigate('/dashboard', { replace: true });
                    } catch (error) {
                      console.error('Manual navigation failed:', error);
                      navigate('/dashboard', { replace: true });
                    }
                  }}
                  sx={{ mt: 2 }}
                >
                  Go to Dashboard
                </Button>

                <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
                  Click above if automatic redirect doesn't work
                </Typography>
              </>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AutoProvision;